import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AdministrativeStaff from '../components/AdministrativeStaff';
import Notifications from '../components/Notifications';
import Notes from '../components/Notes';
import Layout from '../components/Layout/Layout';
import ClassManagement from '../components/ClassManagement';
import MyAttendance from '../components/MyAttendance';

// Parent-only pages
import ReportCard from '../components/ReportCard';
import ChildAttendance from '../components/ChildAttendance';
import ChildPayments from '../components/ChildPayments';

// ─── Dashboards ───
import AdminDashboard from '../components/Dashboard/AdminDashboard';
import SuperintendentDashboard from '../components/Dashboard/SuperintendentDashboard';
import PrincipalDashboard from '../components/Dashboard/PrincipalDashboard';
import TeacherDashboard from '../components/Dashboard/TeacherDashboard';
import ParentDashboard from '../components/Dashboard/ParentDashboard';
import AccountantDashboard from '../components/Dashboard/AccountantDashboard';
import LibrarianDashboard from '../components/Dashboard/LibrarianDashboard';
import ReceptionistDashboard from '../components/Dashboard/ReceptionistDashboard';
import LabAttendantDashboard from '../components/Dashboard/LabAttendantDashboard';
import ReceptionDesk from '../components/ReceptionDesk'; 

// ─── Management pages ───
import UserManagement from '../components/UserManagement';
import CampusManagement from '../components/CampusManagement';
import BulkUpload from '../components/BulkUpload';
import StaffManagement from '../components/StaffManagement';
import SalaryManagement from '../components/SalaryManagement';
import FeeManagement from '../components/FeeManagement';
import StudentManagement from '../components/StudentManagement';
import LibraryManagement from '../components/LibraryManagement';
import FeeStructureManager from '../components/FeeStructureManager';
import Reports from '../components/Reports';
import AdminSettings from '../components/AdminSettings';
import AttendanceReports from '../components/AttendanceReports';
import ContactsDirectory from '../components/ContactsDirectory';
import AdmissionInquiries from '../components/AdmissionInquiries';

// ─── Staff attendance + integrations + devices ───
import StaffAttendance from '../components/StaffAttendance';
import IntegrationsPanel from '../components/Admin/IntegrationsPanel';
import AttendanceDevices from '../components/AttendanceDevices';

// ─── Teacher-specific ───
import TeacherManagement from '../components/TeacherManagement';
import TeacherTimetable from '../components/Teacher/TeacherTimetable';
import TeacherClasses from '../components/Teacher/TeacherClasses';
import TeacherAttendance from '../components/Teacher/TeacherAttendance';
import TeacherGrades from '../components/Teacher/TeacherGrades';

const Placeholder = ({ title }) => (
  <div className="p-6 bg-white rounded shadow">
    <h2 className="text-xl font-bold mb-4">{title}</h2>
    <p className="text-gray-500">This section is under development.</p>
  </div>
);

const GradeView = () => <Placeholder title="Grades" />;

const NoAccess = () => (
  <div className="p-6 bg-white rounded shadow text-center">
    <i className="fas fa-lock text-4xl text-red-500 mb-3"></i>
    <h2 className="text-xl font-bold mb-2">Access Denied</h2>
    <p className="text-gray-500">You don't have permission to view this section.</p>
  </div>
);

const ACCESS = {
  campus: ['admin', 'superintendent', 'principal'],
  users: ['admin', 'superintendent', 'principal', 'accountant'],
  teachers: ['admin', 'superintendent', 'principal'],
  adminStaff: ['admin', 'superintendent', 'principal'],
  classes: ['admin', 'superintendent', 'principal', 'teacher'],
  students: ['admin', 'superintendent', 'principal', 'accountant', 'teacher'],
  attendance: ['admin', 'superintendent', 'principal', 'accountant', 'teacher'],
  grades: ['admin', 'superintendent', 'principal', 'teacher'],
  fees: ['admin', 'superintendent', 'principal', 'accountant'],
  staff: ['admin', 'superintendent', 'principal', 'accountant'],
  salaries: ['admin', 'accountant', 'principal', 'superintendent'],
  notifications: ['admin', 'superintendent', 'principal', 'accountant', 'librarian', 'receptionist', 'lab_attendant', 'teacher', 'parent'],
  notes: ['admin', 'superintendent', 'principal', 'teacher', 'parent'],
  library: ['admin', 'superintendent', 'principal', 'teacher', 'librarian'],
  bulk: ['admin', 'superintendent', 'principal'],
  reports: ['admin', 'superintendent', 'principal', 'accountant'],
  timetable: ['admin', 'superintendent', 'principal', 'teacher'],
  staffAttendance: ['admin', 'superintendent', 'principal', 'accountant'],
  myAttendance: ['teacher', 'librarian', 'receptionist', 'lab_attendant'],
  integrations: ['admin'],
  devices: ['admin'],
  reportCard: ['parent'],
  childAttendance: ['parent'],
  childPayments: ['parent'],
  receptionDesk: ['admin', 'superintendent', 'principal', 'receptionist'],
  contactsDirectory: ['admin', 'superintendent', 'principal', 'receptionist'],
  admissionInquiries: ['admin', 'superintendent', 'principal', 'receptionist'],
};

export default function DashboardRouter() {
  const { user } = useAuth();
  const role = user?.role || 'admin';

  const roleMap = {
    admin: AdminDashboard,
    superintendent: SuperintendentDashboard,
    principal: PrincipalDashboard,
    teacher: TeacherDashboard,
    parent: ParentDashboard,
    accountant: AccountantDashboard,
    librarian: LibrarianDashboard,
    receptionist: ReceptionistDashboard,
    lab_attendant: LabAttendantDashboard,
  };
  const DashboardComponent = roleMap[role] || AdminDashboard;
  const can = (section) => ACCESS[section]?.includes(role);

  return (
    <Layout>
      <Routes>
        <Route index element={<DashboardComponent />} />

        <Route path="campus" element={can('campus') ? <CampusManagement /> : <NoAccess />} />
        <Route path="users" element={can('users') ? <UserManagement /> : <NoAccess />} />

        <Route
          path="classes"
          element={
            !can('classes') ? <NoAccess />
            : role === 'teacher' ? <TeacherClasses />
            : <ClassManagement />
          }
        />

        <Route path="students" element={can('students') ? <StudentManagement /> : <NoAccess />} />

        <Route
          path="attendance"
          element={
            !can('attendance') ? <NoAccess />
            : role === 'teacher' ? <TeacherAttendance />
            : <AttendanceReports />
          }
        />

        <Route
          path="grades"
          element={
            !can('grades') ? <NoAccess />
            : role === 'teacher' ? <TeacherGrades />
            : <GradeView />
          }
        />

        <Route
          path="fee-structures"
          element={
            ['admin', 'accountant'].includes(role)
              ? <FeeStructureManager />
              : <NoAccess />
          }
        />

        <Route path="fees" element={can('fees') ? <FeeManagement /> : <NoAccess />} />
        <Route path="staff" element={can('staff') ? <StaffManagement /> : <NoAccess />} />
        <Route path="salaries" element={can('salaries') ? <SalaryManagement /> : <NoAccess />} />
        <Route path="library" element={can('library') ? <LibraryManagement /> : <NoAccess />} />
        <Route path="bulk" element={can('bulk') ? <BulkUpload /> : <NoAccess />} />
        <Route path="reports" element={can('reports') ? <Reports /> : <NoAccess />} />
        <Route path="admin-settings" element={role === 'admin' ? <AdminSettings /> : <NoAccess />} />

        <Route
          path="timetable"
          element={
            !can('timetable') ? <NoAccess />
            : role === 'teacher' ? <TeacherTimetable />
            : <Placeholder title="Timetable" />
          }
        />

        <Route path="notifications" element={can('notifications') ? <Notifications /> : <NoAccess />} />
        <Route path="notes" element={can('notes') ? <Notes /> : <NoAccess />} />

        {/* ─── Parent-only pages ─── */}
        <Route path="report-card" element={can('reportCard') ? <ReportCard /> : <NoAccess />} />
        <Route path="child-attendance" element={can('childAttendance') ? <ChildAttendance /> : <NoAccess />} />
        <Route path="child-payments" element={can('childPayments') ? <ChildPayments /> : <NoAccess />} />

        <Route path="teachers" element={can('teachers') ? <TeacherManagement /> : <NoAccess />} />
        <Route path="admin-staff" element={can('adminStaff') ? <AdministrativeStaff /> : <NoAccess />} />
        <Route path="staff-attendance" element={can('staffAttendance') ? <StaffAttendance /> : <NoAccess />} />
        <Route path="my-attendance" element={can('myAttendance') ? <MyAttendance /> : <NoAccess />} />
        <Route path="integrations" element={can('integrations') ? <IntegrationsPanel /> : <NoAccess />} />
        <Route path="devices" element={can('devices') ? <AttendanceDevices /> : <NoAccess />} />
        <Route path="contacts" element={can('contactsDirectory') ? <ContactsDirectory /> : <NoAccess />} />
        <Route path="inquiries" element={can('admissionInquiries') ? <AdmissionInquiries /> : <NoAccess />} />
        <Route path="*" element={<Navigate to="/dashboard" />} />
      </Routes>
    </Layout>
  );
}